import { useRef } from 'preact/compat'
import { useDispatch, useSelector } from 'react-redux'
import { getJWT } from '../api/cardinalCruise'
import { validateThreeDS } from '../api/payments'
import {
  ERROR_RESPONSES,
  SUCCESS_RESPONSES,
} from '../constants/serverResponses'
import { cardinalCruiseUpdate , cardinalCruiseSelector } from '../redux/slice/cardinalCruiseSlice'
import {
  authoriseError,
  updatePaymentPending,
  paymentResponseSelector,
} from '../redux/slice/paymentResponseSlice'
import useScript from './useScript'
import { config } from '../config'
import {
  CARDINAL_STATUSES,
  CARDINAL_CANCEL_REASONS,
  CARDINAL_LIB_STATUSES,
  CARDINAL_CONNECTIVITY_ERRORS,
  CARDINAL_GENERAL_ERRORS,
} from '../constants/cardinal'
import { DataDog } from '../datadog'
import useTimeOut from './useTimeOut'
import useCardinalDDC from './useCardinalDDC'
import { billingAddressSelector } from '../redux/slice/billingAddressSlice'
import { transactionDetailsSelector } from '../redux/slice/transactionDetailsSlice'
import { cardsSelector } from '../redux/slice/cardsSlice'

export const cardinalTriggerHandler = (cardNumber, onSuccess, onFailure) =>
  window.Cardinal.trigger('bin.process', cardNumber)
    .then(onSuccess)
    .catch(onFailure)

export default function useCardinal({ setPayButtonVisible }) {
  const dispatch = useDispatch()
  const transactionDetails = useSelector(transactionDetailsSelector)
  const paymentResponse = useSelector(paymentResponseSelector)
  const cardinalCruise = useSelector(cardinalCruiseSelector)
  const billingAddress = useSelector(billingAddressSelector)
  const cards = useSelector(cardsSelector)
  const { cardinalStatus, isCardinalInitialised } = cardinalCruise
  const { amount, successUrl } = transactionDetails
  const isCardinalInitialisedRef = useRef(null)

  const collectCardinalDDC = useCardinalDDC()

  const onTimeoutStart = useTimeOut(() => setPayButtonVisible(true))

  const initCardinal = async onSetupCompleted => {
    if ('Cardinal' in window) {
      const { Cardinal } = window

      const cardId = cards?.selectedCard?.id

      const {
        responseData: { token },
      } = await getJWT(amount, billingAddress, cardId)

      dispatch(
        cardinalCruiseUpdate({
          cardinalJwt: token,
        }),
      )

      if (isCardinalInitialised || isCardinalInitialisedRef.current) {
        Cardinal.trigger('jwt.update', token)

        collectCardinalDDC(onSetupCompleted)

        return
      }

      Cardinal.configure({
        logging: {
          level: 'on',
        },
      })

      Cardinal.setup('init', {
        jwt: token,
      })

      Cardinal.on('payments.validated', async (data, cardinalJwt) => {
        DataDog.info(`Cardinal.on('payments.validated')`, JSON.stringify(data))

        if (
          cardinalStatus === CARDINAL_STATUSES.connectivity &&
          data?.ErrorNumber === CARDINAL_GENERAL_ERRORS.GeneralError
        ) {
          DataDog.info(
            `CardinalCruise general error after connectivity error.`,
            JSON.stringify(data),
          )
          dispatch(
            cardinalCruiseUpdate({
              cardinalStatus: CARDINAL_STATUSES.connectivity,
            }),
          )
          dispatch(updatePaymentPending(false))
          setPayButtonVisible(true)
          return
        }

        if (
          Object.values(CARDINAL_CONNECTIVITY_ERRORS).includes(
            data?.ErrorNumber,
          )
        ) {
          DataDog.info(`CardinalCruise internal error.`, JSON.stringify(data))
          dispatch(
            cardinalCruiseUpdate({
              cardinalStatus: CARDINAL_STATUSES.connectivity,
            }),
          )
          dispatch(updatePaymentPending(false))
          setPayButtonVisible(true)
          return
        }

        if (!data?.Validated && !data.Payment) {
          DataDog.info(`CardinalCruise validation error.`, JSON.stringify(data))
          return
        }

        const validate = () => validateThreeDS(cardinalJwt)

        switch (data.ActionCode) {
          case 'SUCCESS':
            dispatch(
              cardinalCruiseUpdate({
                cardinalStatus: CARDINAL_STATUSES.success,
              }),
            )

            try {
              const { responseData, responseStatus } = await validate()

              if (responseStatus === SUCCESS_RESPONSES.OK) {
                dispatch(
                  cardinalCruiseUpdate({
                    successRedirectUrl: successUrl,
                  }),
                )
              } else if (
                responseStatus === ERROR_RESPONSES.USER_DETAILS_ERROR ||
                responseStatus === ERROR_RESPONSES.USER_AUTHORIZE_ERROR ||
                responseStatus === ERROR_RESPONSES.SERVER_ERROR ||
                responseStatus === ERROR_RESPONSES.NOT_FOUND_ERROR
              ) {
                dispatch(
                  cardinalCruiseUpdate({
                    cardinalStatus: CARDINAL_STATUSES.failure,
                  }),
                )

                dispatch(
                  authoriseError({
                    responseStatus,
                    errorCode: responseData?.errorCode,
                    errorMessage: responseData?.errorMessage,
                    isRetryable: responseData?.isRetryable,
                  }),
                )
              } else {
                dispatch(updatePaymentPending(false))
                setPayButtonVisible(true)

                dispatch(
                  cardinalCruiseUpdate({
                    cardinalStatus: CARDINAL_STATUSES.connectivity,
                  }),
                )
              }
            } catch (e) {
              dispatch(
                cardinalCruiseUpdate({
                  cardinalStatus: CARDINAL_STATUSES.failure,
                }),
              )
            }

            break

          case 'FAILURE':
            if (
              data.Payment.ExtendedData.ChallengeCancel !==
              CARDINAL_CANCEL_REASONS.clickCancelButton
            ) {
              dispatch(updatePaymentPending(true))
              const { responseData } = await validate()

              dispatch(
                cardinalCruiseUpdate({
                  cardinalStatus: CARDINAL_STATUSES.failure,
                }),
              )

              dispatch(
                authoriseError({
                  responseStatus: ERROR_RESPONSES.USER_DETAILS_ERROR,
                  isRetryable: responseData?.isRetryable,
                }),
              )
              dispatch(updatePaymentPending(false))
            } else {
              setPayButtonVisible(true)
            }
            break

          case 'NOACTION':
          case 'ERROR':
          default:
            DataDog.warn(`CardinalCruise NOACTION or ERROR.`)
            dispatch(
              cardinalCruiseUpdate({
                cardinalStatus: CARDINAL_STATUSES.error,
              }),
            )
            dispatch(updatePaymentPending(false))
            setPayButtonVisible(true)
            break
        }

        dispatch(updatePaymentPending(false))
      })

      Cardinal.on('payments.setupComplete', setupCompleteData => {
        DataDog.info(
          `Cardinal.on('payments.setupComplete')`,
          JSON.stringify(setupCompleteData),
        )
        onTimeoutStart()
        dispatch(
          cardinalCruiseUpdate({
            cardinalJwt: token,
            cardinalSessionId: setupCompleteData.sessionId,
            libStatus: CARDINAL_LIB_STATUSES.ready,
          }),
        )

        collectCardinalDDC(onSetupCompleted)
      })

      dispatch(
        cardinalCruiseUpdate({
          isCardinalInitialised: true,
        }),
      )

      isCardinalInitialisedRef.current = true

      DataDog.info(`Cardinal initialised`)
    }
  }

  const handleScriptLoadError = () => {
    dispatch(
      cardinalCruiseUpdate({
        libStatus: CARDINAL_LIB_STATUSES.failed,
      }),
    )
  }

  const getCardinalScript = () => {
    if (process.env.NODE_ENV === 'dev') {
      return config.devCardinalCruise
    }

    return `${config.baseURL}songbird.js`
  }

  useScript(getCardinalScript(), () => {}, handleScriptLoadError, false)

  return initCardinal
}
